import { skipToken, useSuspenseQuery } from "@apollo/client";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { DashboardTile } from "components/Dashboard/DashboardTile";
import { gql } from "generated-graphql";
import { FacilityWasteView } from "generated-graphql/graphql";
import { ReactNode, useMemo } from "react";
import { useParams } from "react-router-dom";

import { useWasteFacility } from "../../useWasteFacility";
import { TileContentGreyBackground } from "../TileContentGreyBackground";
import { useHasWasteShipmentData } from "../useHasWasteShipmentData";
import { useWasteFacilityState } from "../WasteFacilityContext";
import { HazardousWasteShipmentsGraph } from "./HazardousWasteShipmentsGraph";
import { HighestGeneratingWasteStreamsGraph } from "./HighestGeneratingWasteStreamsGraph";
import { type AllowedGraphType, AllowedGraphTypes } from "./utils";
import { WasteShipmentsByMonthGraph } from "./WasteShipmentsByMonthGraph";

const WASTE_SHIPMENT_CHARTS = gql(`
  query WasteShipmentCharts($facilityId: ID!, $filter: FacilityWasteShipmentsFilter!) {
    facilityWasteShipmentCharts(facilityId: $facilityId, filter: $filter) {
      wasteShipmentsByMonth {
        totalAllHazardousAmount
        totalAllWasteAmount
        totalAcutelyHazardousAmount
        totalHazardousAmount
        totalStateRegulatedAmount
        totalNonhazardousAmount
        highestCalculatedGeneratorStatus
        byMonth {
          month
          vendorHazardousAmount
          eManifestHazardousAmount
          unifiedHazardousAmount
          vendorAcutelyHazardousAmount
          eManifestAcutelyHazardousAmount
          unifiedAcutelyHazardousAmount
          unifiedNonhazardousAmount
          vendorStateRegulatedAmount
          eManifestStateRegulatedAmount
          unifiedStateRegulatedAmount
          reportedFederalGeneratorStatus
          reportedStateGeneratorStatus
          calculatedFederalGeneratorStatus
          calculatedStateGeneratorStatus
          isOverReportedThreshold
        }
      }
      highestGeneratingWasteStreams {
        wasteStream {
          id
          name
        }
        amount
      }
    }
  }
`);

const ingestedMessage = "Based on data ingested by Encamp";

const graphVariations: Record<
  AllowedGraphType,
  { label: string; selectLabel: string; tooltip: ReactNode }
> = {
  hazardousWaste: {
    label: "Hazardous waste shipments in lbs",
    selectLabel: "Hazardous waste shipments",
    tooltip: (
      <Typography variant="caption">
        {ingestedMessage}, see how your historical RCRA hazardous waste shipments relate to federal
        generator status thresholds. If a threshold is exceeded in a calendar month, you may need to
        file an update notification with the EPA.
      </Typography>
    ),
  },
  hazardousWasteByMonth: {
    label: "Waste shipments by month in lbs",
    selectLabel: "Waste shipments by month",
    tooltip: (
      <Typography variant="caption">
        {ingestedMessage}, see how your historical waste shipments are broken down by
        classification.
      </Typography>
    ),
  },
  highestGeneratingWasteStreams: {
    label: "Highest waste stream shipments in lbs",
    selectLabel: "Highest waste stream shipments",
    tooltip: (
      <Typography variant="caption">
        {ingestedMessage}, see the waste streams at your facility that contribute the most to your
        waste shipments.
      </Typography>
    ),
  },
};

export type WasteShipmentFilter = {
  hazardousWaste: { focus?: "acutelyHazardous" | "hazardous" };
};

export const WasteShipmentsTile = () => {
  return (
    <DashboardTile size={12}>
      <WasteShipmentsContent />
    </DashboardTile>
  );
};

function WasteShipmentsContent() {
  const theme = useTheme();

  const { facilityId } = useParams<{ facilityId: string }>();
  const { data: facilityData } = useWasteFacility(facilityId);
  const {
    wasteFacilityState: { optimisticFilters, deferredFilters },
    setWasteFacilityState,
    effectiveWasteFacilityId,
  } = useWasteFacilityState();

  const selectedGraphType =
    optimisticFilters.graphType && AllowedGraphTypes.includes(optimisticFilters.graphType)
      ? optimisticFilters.graphType
      : "hazardousWaste";

  const hasDateFilters = Boolean(
    (optimisticFilters.startDate && optimisticFilters.endDate) ||
    (deferredFilters.startDate && deferredFilters.endDate)
  );

  const skip = useMemo(() => {
    return !deferredFilters.startDate || !deferredFilters.endDate;
  }, [deferredFilters]);

  const filters = useMemo(() => {
    return {
      facilityId: effectiveWasteFacilityId,
      filter: {
        startDate: deferredFilters.startDate,
        endDate: deferredFilters.endDate,
        view: deferredFilters.view ?? FacilityWasteView.All,
        tags: deferredFilters.tags,
      },
    };
  }, [deferredFilters, effectiveWasteFacilityId]);

  const { data } = useSuspenseQuery(
    WASTE_SHIPMENT_CHARTS,
    skip
      ? skipToken
      : {
          variables: {
            ...filters,
          },
        }
  );

  const renderGraph = useMemo(() => {
    if (!data || !hasDateFilters) {
      return (
        <Stack gap={2}>
          <Stack gap={2} direction="row">
            <Skeleton variant="rounded" height={80} width={180} />
            <Skeleton variant="rounded" height={80} width={180} />
            <Skeleton variant="rounded" height={80} width={180} />
          </Stack>
          <Skeleton variant="rounded" height={40} />
          <Skeleton variant="rounded" height={350} />
          <Skeleton variant="rounded" height={50} />
        </Stack>
      );
    }
    switch (selectedGraphType) {
      case "hazardousWaste":
        return (
          <HazardousWasteShipmentsGraph
            data={data.facilityWasteShipmentCharts.wasteShipmentsByMonth}
            facilityState={facilityData?.facility?.state}
          />
        );
      case "highestGeneratingWasteStreams":
        return (
          <HighestGeneratingWasteStreamsGraph
            data={data.facilityWasteShipmentCharts.highestGeneratingWasteStreams}
          />
        );
      case "hazardousWasteByMonth":
        return (
          <WasteShipmentsByMonthGraph
            data={data.facilityWasteShipmentCharts.wasteShipmentsByMonth}
          />
        );
      default:
        return null;
    }
  }, [data, hasDateFilters, selectedGraphType, facilityData?.facility?.state]);

  const hasShipmentData = useHasWasteShipmentData();

  return (
    <>
      <DashboardTile.Header
        title="Waste Shipments"
        infoTooltip={
          <Typography variant="caption">
            {ingestedMessage}, use the dropdown on the right to view historical information about
            this facility's waste shipments. If both vendor and e‑Manifest data is present for a
            manifest, we will display vendor data.
          </Typography>
        }
      >
        <Stack direction="row" spacing={2}>
          <FormControl sx={{ width: theme.spacing(32.5) }}>
            <InputLabel id="view-by">View by</InputLabel>
            <Select
              sx={{ height: theme.spacing(5) }}
              fullWidth
              labelId="view-by"
              label="View by"
              value={selectedGraphType}
              onChange={(event) =>
                setWasteFacilityState((f) => {
                  f.graphType = event.target.value as AllowedGraphType;
                })
              }
            >
              {Object.entries(graphVariations).map(([key, { selectLabel }]) => (
                <MenuItem key={key} value={key}>
                  {selectLabel}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DashboardTile.Header>

      <DashboardTile.ContentArea sx={{ flex: 1 }}>
        {hasShipmentData ? (
          renderGraph
        ) : (
          <TileContentGreyBackground>🧾 No shipment data to show</TileContentGreyBackground>
        )}
      </DashboardTile.ContentArea>
    </>
  );
}
